"use client";

import { storyblokEditable } from "@storyblok/react/rsc";
import { useState } from "react";

import { Rails } from "~components/ui";
import { SeedWave } from "~components/ui/seed-wave";
import { cn } from "~utils";

import { RichText } from "../rich-text";

import { TileSectionCTA } from "./components/tile-section-cta";
import { TileSectionItem } from "./components/tile-section-item";
import type { TileSectionProps } from "./types";

export function TileSection({ blok, ...context }: TileSectionProps) {
  const [isPrevDisabled, setIsPrevDisabled] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const textColor =
    blok.backgroundVariant === "contrast"
      ? "text-content-heading-contrast"
      : "text-content-heading";

  return (
    <Rails.Root
      id={blok.sectionId}
      data-section-type="tile"
      className={cn(
        "section relative overflow-hidden",
        blok.theme,
        blok.backgroundVariant === "contrast" && "bg-surface/contrast",
        blok.backgroundVariant === "default" && "bg-surface/100",
        blok.backgroundVariant === "darker" && "bg-surface/200",
      )}
      {...storyblokEditable(blok)}
    >
      <div className="z-10 flex flex-wrap justify-between gap-lg">
        <div className="flex grow flex-col gap-sm">
          {blok.leading && (
            <div
              className={cn("t-prosi-xl max-w-leading text-balance", textColor)}
            >
              {blok.leading}
            </div>
          )}

          <h1
            className={cn(
              "t-strong-3xl max-w-title/sm text-balance",
              textColor,
            )}
          >
            {blok.title}
          </h1>

          <RichText
            className={cn("t-strong-lg max-w-sub text-pretty", textColor)}
            data={blok.text}
            {...context}
          />

          <TileSectionCTA blok={blok} {...context} />
        </div>

        <div
          className={cn(
            "flex gap-sm place-self-end",
            isPrevDisabled && isNextDisabled && "hidden",
          )}
        >
          <Rails.Control
            action="prev"
            isDisabled={isPrevDisabled}
            onDisabledChanged={setIsPrevDisabled}
          />
          <Rails.Control
            action="next"
            isDisabled={isNextDisabled}
            onDisabledChanged={setIsNextDisabled}
          />
        </div>
      </div>

      <Rails.Viewport
        className={cn(
          "section-item-full z-10 flex gap-sm-md overflow-x-auto md:gap-lg",
          "scrollbar-hidden z-10 snap-x snap-mandatory scroll-px-[var(--section-offset-x)] px-[var(--section-offset-x)]",
        )}
      >
        {blok.items.map((item, index) => (
          <TileSectionItem
            mainBlok={blok}
            key={item._uid}
            blok={item}
            index={index}
            isNumbered={blok.isNumbered}
            {...context}
          />
        ))}
      </Rails.Viewport>

      {blok.withSeedsPattern && (
        <SeedWave
          className="section-item-full absolute top-[0] z-0 h-full w-full bg-decor/200"
          seedPatternColor={blok.seedPatternColor}
        />
      )}
    </Rails.Root>
  );
}
